'use client';

import type { BrandBarProps } from './types';
import { useState, useEffect, useCallback } from 'react';
import { ClearIcon } from '@pixum/svg-icon/lib/Standard/Clear';
import Handle from '@pixum/svg-icon/lib/Standard/Handle';
import Button, { ButtonVariants } from '@pixum/button';
import { util } from '@pixum/std-lib';
import styles from './styles.module.css';
import { setCookie, isBrowser, trackEvent, TRACKING_ACTIONS, TRACKING_CATEGORIES } from 'utils';
import { useDeviceInfo } from 'hooks';
import { richTextJsonToString } from '../richtText';
import { BRAND_BAR_OPTIONS } from '../../constants';
import { BRAND_THEME } from '@pixum/theme-provider';

type BrandBarClass = (typeof BRAND_BAR_OPTIONS)[keyof typeof BRAND_BAR_OPTIONS];

/**
 * BrandBar Component
 *
 * A promotional banner displayed at the top of eligible pages (e.g., landing pages or start pages).
 * Used to highlight marketing campaigns, special offers, milestones, or other announcements.
 *
 * Features:
 * - **Content Managed via CMS**: Content (`htmlText`), visibility (`show`), and default state (`openByDefault`)
 *   are all driven by CMS configuration (e.g., Contentful).
 * - **Persistent Visibility**:
 *   - Remembers the user's preference using a `brandBarClose` cookie.
 *   - If the user closes the BrandBar, it stays closed on future visits.
 *   - If left open (and not closed manually), the bar respects CMS `openByDefault`.
 * - **Responsive Behavior**:
 *   - Automatically hidden on mobile devices.
 *   - Only visible on desktop
 * - **Client-Only Rendering**:
 *   - Uses hydration guard to avoid SSR/CSR mismatch (`useEffect` with `hydrated` state).
 *   - Safe access to cookies and window APIs only after client render.
 * - **Accessible**:
 *   - Includes `role="alert"` for screen readers.
 *   - Close and toggle buttons are labeled for accessibility.
 * - **Analytics Tracking**:
 *   - Tracks open/close actions using `trackEvent` with relevant categories and actions.
 *
 * @component
 * @param props - Props controlling visibility, content, and behavior.
 * @returns The rendered brand bar banner, or null if hidden or not hydrated.
 */
function BrandBar({ openByDefault = false, htmlText, show }: BrandBarProps) {
  const [hydrated, setHydrated] = useState(false);
  const [brandBarClass, setBrandBarClass] = useState<BrandBarClass>(BRAND_BAR_OPTIONS.close);
  const currentUrl = isBrowser() ? util.parseUrl().host + util.parseUrl().path : '';
  const { isMobile } = useDeviceInfo();

  const evaluateInitialVisibility = useCallback(() => {
    const cookieMatch = document.cookie.match(/brandBarClose=([a-z]+);?/);

    const cookieSaysOpen = (cookieMatch && cookieMatch[1] === 'false') || (!cookieMatch && openByDefault);

    return !isMobile && cookieSaysOpen;
  }, [openByDefault]);

  useEffect(() => {
    setHydrated(true);

    if (!show || !isBrowser()) return;

    const shouldOpen = evaluateInitialVisibility();

    setBrandBarClass(shouldOpen ? BRAND_BAR_OPTIONS.open : BRAND_BAR_OPTIONS.close);
  }, [evaluateInitialVisibility, show]);

  const closeBrandBar = () => {
    trackEvent(TRACKING_CATEGORIES.BRAND_BAR, TRACKING_ACTIONS.CLOSING, currentUrl);
    setBrandBarClass(BRAND_BAR_OPTIONS.close);
    setCookie('brandBarClose', true, { secure: true });
  };

  const openBrandBar = () => {
    trackEvent(TRACKING_CATEGORIES.BRAND_BAR, TRACKING_ACTIONS.OPENING, currentUrl);
    setBrandBarClass(BRAND_BAR_OPTIONS.open);
    setCookie('brandBarClose', false, { secure: true });
  };

  const showBrandBar = isBrowser() && show;
  const showBrandBarClass = showBrandBar ? '' : 'brandBarHidden';
  const text = richTextJsonToString(htmlText);
  const theme = `theme__base-${BRAND_THEME.base} theme__accent-${BRAND_THEME.accent} theme__brightness-${BRAND_THEME.brightness}`;

  if (!hydrated) return null;

  return (
    <div className={`${styles.brandBar} ${brandBarClass} transition ${showBrandBarClass}`} id="brand-bar">
      <div className={`${styles.brandBarContent} ${theme}`} role="alert">
        <div className={`${styles.brandBarHeadline} text--caption`} dangerouslySetInnerHTML={{ __html: text }}></div>
        <Button
          className={styles.brandBarClose}
          label="brand-bar-close"
          onClick={closeBrandBar}
          variant={ButtonVariants.TEXT}
        >
          <ClearIcon color="white" />
        </Button>
      </div>
      <Button
        className={styles.brandBarHandle}
        label="brand-bar-handle"
        onClick={openBrandBar}
        variant={ButtonVariants.TEXT}
      >
        <Handle color="white" />
      </Button>
    </div>
  );
}

export { BrandBar };
